import styled from "styled-components"

import { colors, Text } from "@minuthq/meatball-ui-react"

import { GraphCard } from "src/components/Homes/DeviceDetails/Overview/GraphCard"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchWifiHealthByOrganization } from "src/data/portfolioAnalytics/portfolioAnalyticsQueries"
import { TWifiHealthAnalyticsByOrganization } from "src/data/portfolioAnalytics/types/portfolioAnalyticsTypes"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { BarChart } from "src/ui/Graphs/BarChart"
import { TBarChartMultiSeriesData } from "src/ui/Graphs/graphTypes"
import { MCard } from "src/ui/MCard/MCard"
import { spacing } from "src/ui/spacing"
import { getStoredSelectedTimezone } from "src/utils/l10n"

const GRAPH_HEIGHT = 300

export function PortfolioAnalyticsWifiHealthGraph() {
  const { tPlaceholder } = useTranslate()
  const user = useGetUser()
  const timezone = getStoredSelectedTimezone()
  const { orgId } = useOrganization()

  const fetchWifiHealthByOrganization = useFetchWifiHealthByOrganization({
    orgId,
  })

  const analytics = fetchWifiHealthByOrganization.data?.analytics
  const { isLoading } = fetchWifiHealthByOrganization

  function hasNonZeroTotal(
    item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
  ) {
    const total =
      item.aggregation.excellent +
      item.aggregation.good +
      item.aggregation.fair +
      item.aggregation.poor +
      item.aggregation.none +
      item.aggregation.no_devices
    return total > 0
  }

  const wifiHealthData: TBarChartMultiSeriesData[] = [
    {
      name: tPlaceholder("Excellent"),
      data:
        analytics?.flatMap(
          (
            analyticsGroup: TWifiHealthAnalyticsByOrganization["analytics"][number]
          ) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map(
                (
                  item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
                ): [string, number] => [
                  item.metadata.homegroup_name,
                  item.aggregation.excellent,
                ]
              )
        ) ?? [],
      fillColor: colors.systemGoodLight,
      borderColor: colors.systemGoodDark,
    },
    {
      name: tPlaceholder("Good"),
      data:
        analytics?.flatMap(
          (
            analyticsGroup: TWifiHealthAnalyticsByOrganization["analytics"][number]
          ) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map(
                (
                  item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
                ): [string, number] => [
                  item.metadata.homegroup_name,
                  item.aggregation.good,
                ]
              )
        ) ?? [],
      fillColor: colors.systemInfoLight,
      borderColor: colors.systemInfoDark,
    },
    {
      name: tPlaceholder("Fair"),
      data:
        analytics?.flatMap(
          (
            analyticsGroup: TWifiHealthAnalyticsByOrganization["analytics"][number]
          ) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map(
                (
                  item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
                ): [string, number] => [
                  item.metadata.homegroup_name,
                  item.aggregation.fair,
                ]
              )
        ) ?? [],
      fillColor: colors.systemWarningLight,
      borderColor: colors.systemWarningDark,
    },
    {
      name: tPlaceholder("Poor"),
      data:
        analytics?.flatMap(
          (
            analyticsGroup: TWifiHealthAnalyticsByOrganization["analytics"][number]
          ) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map(
                (
                  item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
                ): [string, number] => [
                  item.metadata.homegroup_name,
                  item.aggregation.poor,
                ]
              )
        ) ?? [],
      fillColor: colors.systemErrorLight,
      borderColor: colors.systemErrorDark,
    },
    {
      name: tPlaceholder("No Signal"),
      data:
        analytics?.flatMap(
          (
            analyticsGroup: TWifiHealthAnalyticsByOrganization["analytics"][number]
          ) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map(
                (
                  item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
                ): [string, number] => [
                  item.metadata.homegroup_name,
                  item.aggregation.none,
                ]
              )
        ) ?? [],
      fillColor: colors.systemErrorDark,
      borderColor: colors.systemErrorDark,
    },
    {
      name: tPlaceholder("No Devices"),
      data:
        analytics?.flatMap(
          (
            analyticsGroup: TWifiHealthAnalyticsByOrganization["analytics"][number]
          ) =>
            analyticsGroup.data
              .filter(hasNonZeroTotal)
              .map(
                (
                  item: TWifiHealthAnalyticsByOrganization["analytics"][number]["data"][number]
                ): [string, number] => [
                  item.metadata.homegroup_name,
                  item.aggregation.no_devices,
                ]
              )
        ) ?? [],
      fillColor: colors.neutralDark,
      borderColor: colors.neutralDark2,
    },
  ]

  return (
    <GraphCard
      title={tPlaceholder("WiFi Health")}
      titleVariant="heading3"
      info={tPlaceholder("Monitor the WiFi signal quality across your units.")}
      isLoading={isLoading}
    >
      <BarChart
        dataSeriesType="multi"
        data={wifiHealthData}
        timezone={timezone.timezone.tzCode}
        stackSeries
        clockType={user.clock_type}
        height={GRAPH_HEIGHT}
        borderRadius={2}
        seriesHoverBrightness={0.03}
        interactive
        yAxisOptions={{
          title: {
            text: tPlaceholder("Number of units"),
          },
        }}
        xAxisOptions={{
          type: "category",
        }}
        tooltip={{
          decimals: 0,
          unit: "",
          formatter: ({ points }) => {
            const total =
              points?.reduce((sum, point) => sum + (point.y ?? 0), 0) ?? 0

            return (
              <MCard padding={0}>
                <TooltipTitle>
                  <Text variant="bodyS">{points?.[0]?.name}</Text>
                </TooltipTitle>
                <PointsContainer>
                  {points?.map((point) => {
                    return (
                      <div key={point.series.name}>
                        <Text variant="subtitleS">
                          {tPlaceholder(
                            `${point.y} units - ${point.series.name}`
                          )}
                        </Text>
                        <Text variant="bodyS">
                          {total > 0
                            ? `(${(((point.y ?? 0) / total) * 100).toFixed(0)}%)`
                            : "(0%)"}
                        </Text>
                      </div>
                    )
                  })}
                </PointsContainer>
              </MCard>
            )
          },
        }}
      />
    </GraphCard>
  )
}

const TooltipTitle = styled.div`
  background-color: ${colors.primaryLight};
  padding: ${spacing.XS} ${spacing.M};
`

const PointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
  padding: ${spacing.XS} ${spacing.M};
`
